import type { Eruda } from 'eruda';
import { createDevToolsDrawer, type DevToolsDrawer } from './devToolsDrawer';

/**
 * Lifecycle wrapper around the eruda overlay — the console / network / storage inspector shown inside
 * the Acomba desktop host.
 *
 * eruda is pulled in through a dynamic import so it lands in its own chunk. One built image serves
 * every environment (`VITE_APP_ENV` is injected into `window.__ENV__` at container startup), so the
 * chunk cannot be stripped at build time — but a production browser never requests it, because the
 * gate in initDevTools never calls in here.
 *
 * Mounting is what starts the capture: eruda hooks `console`, `window.onerror` and `fetch` at init, and
 * only sees what happens afterwards. So the panel is mounted at boot and merely kept invisible until the
 * unlock gesture, rather than being mounted on unlock.
 *
 * eruda ships as a bottom sheet; its `inline` option makes it fill a container of our choosing instead
 * (and drops its floating entry button), which is how the panel ends up in the right-hand drawer.
 */

let panel: Eruda | null = null;
let drawer: DevToolsDrawer | null = null;

/**
 * Bumped by every unmount, and captured by each load. A boolean "is it wanted" cannot tell "still
 * unwanted" from "unmounted and wanted again", and the second case is the one that hurts: a load whose
 * import was in flight across an unmount/remount would otherwise init eruda a second time and leak the
 * first drawer, listeners and all.
 */
let generation = 0;

/** In-flight load, shared by concurrent mount calls so eruda is only ever initialised once. */
let loading: Promise<void> | null = null;

async function loadPanel(loadedForGeneration: number): Promise<void> {
    const eruda = (await import('eruda')).default;

    // An unmount landed while the chunk was in flight — honour it instead of leaving a live overlay.
    if (loadedForGeneration !== generation) return;

    const nextDrawer = createDevToolsDrawer();
    eruda.init({
        container: nextDrawer.mount,
        inline: true,
        autoScale: false,
        defaults: { transparency: 1 },
    });

    // init resets the mount with `all: initial`, collapsing it to an auto-sized inline box. These land
    // after that reset in the same inline declaration, so they win and the panel fills the drawer.
    nextDrawer.mount.style.display = 'block';
    nextDrawer.mount.style.width = '100%';
    nextDrawer.mount.style.height = '100%';

    drawer = nextDrawer;
    panel = eruda;
}

/**
 * Loads and initialises the overlay, hidden. Idempotent and safe to call on every session change.
 *
 * Rejects if the eruda chunk cannot be fetched, and drops the rejected promise rather than caching it —
 * otherwise a single 404 (a stale hashed chunk after a dev redeploy) would poison every later mount.
 */
export async function mountDevToolsPanel(): Promise<void> {
    loading ??= loadPanel(generation).catch((error: unknown) => {
        loading = null;
        throw error;
    });
    await loading;
}

/** Tears the overlay down and unhooks eruda's console / network patches. */
export function unmountDevToolsPanel(): void {
    generation += 1;
    loading = null;
    if (panel === null) return;
    panel.destroy();
    drawer?.destroy();
    panel = null;
    drawer = null;
}

/** Slides the drawer in. No-op until the panel is mounted. */
export function revealDevToolsPanel(): void {
    drawer?.show();
}

/** Hides the drawer, leaving capture running. No-op until the panel is mounted. */
export function concealDevToolsPanel(): void {
    drawer?.hide();
}
